import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ImageIcon, Plus, RefreshCw, Save, X } from 'lucide-react';
import {
  hqEmphasisActivitySchema,
  hqEmphasisDraftSchema,
  hqEmphasisEventId,
  type HqEmphasisActivity,
  type HqEmphasisAttachment,
  type HqEmphasisDocument,
  type HqEmphasisDraft,
  type HqEmphasisLoadResult,
  type HqHistoricalSummary,
} from '@kop/contracts';
import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';
import { registerHqHalfyearNavigationGuard } from '../hq-halfyear-review/navigation-guard';
import { createFirebaseHqEmphasisRepository, type HqEmphasisRepository } from './repository';
import {
  activityCover,
  activityLabel,
  activityTypes,
  activityScopeKey,
  divisionLabel,
  emphasisCounts,
  historicalEmphasisDraft,
} from './model';
import { EmphasisFileViewer, EmphasisPhoto } from './media';
import './workspace.css';

type Props = {
  uid: string;
  initialYear: number;
  initialHalf: 1 | 2;
  historical: HqHistoricalSummary | null;
  repository?: HqEmphasisRepository | null;
  onSaved?: () => void;
};
type Tab = 'overview' | 'activities' | 'media' | 'plan';
const numeric = (value: number | null | undefined) =>
  value == null ? '—' : value.toLocaleString('ko-KR');
const date = (value: string | null) => (value ? value.replaceAll('-', '.') : '일자 미기재');
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const newActivity = (siteId: string | null): HqEmphasisActivity => ({
  id: crypto.randomUUID(),
  type: 'inspection',
  siteId,
  title: '',
  date: null,
  owner: '',
  note: '',
  findings: null,
  actions: null,
  attachments: [],
  coverManifestId: null,
});

export function HqEmphasisWorkspace(props: Props) {
  return <EmphasisSession key={props.uid} {...props} />;
}
function EmphasisSession({
  uid,
  initialYear,
  initialHalf,
  historical,
  repository: provided,
  onSaved,
}: Props) {
  const repository = useMemo(
    () => (provided === undefined ? createFirebaseHqEmphasisRepository(uid) : provided),
    [provided, uid],
  );
  const [period, setPeriod] = useState({ year: initialYear, half: initialHalf });
  const [loaded, setLoaded] = useState<HqEmphasisLoadResult | null>(null),
    [document, setDocument] = useState<HqEmphasisDocument | null>(null);
  const [draft, setDraft] = useState<HqEmphasisDraft | null>(null),
    [baseline, setBaseline] = useState<HqEmphasisDraft | null>(null);
  const [busy, setBusy] = useState(true),
    [saving, setSaving] = useState(false),
    [uploading, setUploading] = useState(false),
    [locked, setLocked] = useState(false);
  const [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [revision, setRevision] = useState(0);
  const [tab, setTab] = useState<Tab>('overview'),
    [site, setSite] = useState('all'),
    [type, setType] = useState('all');
  const [page, setPage] = useState(0),
    [mediaId, setMediaId] = useState(''),
    [mediaPage, setMediaPage] = useState(0),
    [mediaListPage, setMediaListPage] = useState(0);
  const [planStep, setPlanStep] = useState(0),
    [editing, setEditing] = useState<HqEmphasisActivity | null>(null);
  const [viewer, setViewer] = useState<HqEmphasisAttachment | null>(null),
    [showTable, setShowTable] = useState(false);
  const [size, setSize] = useState({ height: 600, width: 1200 });
  const root = useRef<HTMLDivElement>(null),
    generation = useRef(0),
    controller = useRef<AbortController | null>(null);
  const historicalRef = useRef(historical);
  const explicitlySelectedPeriod = useRef(false);
  historicalRef.current = historical;
  const pendingSave = useRef<{ payload: string; commandId: string } | null>(null);
  const cancelRequests = useCallback(() => {
    generation.current++;
    controller.current?.abort();
  }, []);
  const inputBusy = busy || saving || uploading || locked;
  const dirty = !!draft && !!baseline && !same(draft, baseline);
  const canEdit = loaded?.canEdit === true && !locked;
  useEffect(
    () =>
      registerHqHalfyearNavigationGuard(
        () =>
          saving ||
          uploading ||
          (dirty && !window.confirm('저장하지 않은 변경사항을 버리고 이동할까요?')),
      ),
    [saving, uploading, dirty],
  );
  useLayoutEffect(() => {
    if (!root.current) return;
    const observer = new ResizeObserver((entries) => {
      const { height, width } = entries[0]!.contentRect;
      setSize((value) =>
        value.height === height && value.width === width ? value : { height, width },
      );
    });
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const unsubscribe = onSensitiveMemoryCacheClear(() => {
      generation.current++;
      controller.current?.abort();
      setLocked(true);
      setLoaded(null);
      setDocument(null);
      setDraft(null);
      setBaseline(null);
      setViewer(null);
      setEditing(null);
      setShowTable(false);
      setError('');
      setMessage('');
      setBusy(false);
      setSaving(false);
      setUploading(false);
      pendingSave.current = null;
    });
    return () => {
      unsubscribe();
    };
  }, []);
  useEffect(() => {
    const run = ++generation.current;
    setBusy(true);
    setError('');
    setMessage('');
    setDraft(null);
    setBaseline(null);
    setDocument(null);
    setLoaded(null);
    setPage(0);
    setMediaId('');
    setMediaPage(0);
    setMediaListPage(0);
    setViewer(null);
    setEditing(null);
    setShowTable(false);
    if (!repository || locked) {
      setBusy(false);
      return;
    }
    const cached = repository.peek(period);
    if (cached) {
      setLoaded(cached);
      setDocument(cached.document);
    }
    void repository
      .load(period, revision > 0)
      .then((value) => {
        if (generation.current !== run) return;
        setLoaded(value);
        setDocument(value.document);
        const valueDraft =
          value.document?.draft ??
          historicalEmphasisDraft(historicalRef.current, period.year, period.half, value.sites);
        setDraft(valueDraft);
        setBaseline(valueDraft);
      })
      .catch(() => {
        if (generation.current === run) {
          setLoaded(null);
          setError('강조주간 자료를 불러오지 못했습니다. 다시 불러와 주세요.');
        }
      })
      .finally(() => {
        if (generation.current === run) setBusy(false);
      });
    return cancelRequests;
  }, [repository, uid, period, revision, locked, cancelRequests]);
  useEffect(() => {
    if (document || dirty || busy || !loaded || !historical || draft?.historicalReferenceId) return;
    const original = historicalEmphasisDraft(historical, period.year, period.half, loaded.sites);
    if (
      !original.historicalReferenceId &&
      !explicitlySelectedPeriod.current &&
      historical.year === period.year
    ) {
      const latest = [...historical.emphasis].sort((a, b) => b.half - a.half)[0];
      if (latest && latest.half !== period.half) {
        setPeriod({ year: period.year, half: latest.half });
        return;
      }
    }
    if (original.historicalReferenceId) {
      setDraft(original);
      setBaseline(original);
    }
  }, [
    document,
    dirty,
    busy,
    loaded,
    historical,
    draft?.historicalReferenceId,
    period.year,
    period.half,
  ]);
  useEffect(() => {
    if (!dirty) return;
    const prevent = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', prevent);
    return () => window.removeEventListener('beforeunload', prevent);
  }, [dirty]);
  const changePeriod = (next: typeof period) => {
    if (inputBusy || (dirty && !window.confirm('저장하지 않은 변경사항을 버리고 이동할까요?')))
      return;
    explicitlySelectedPeriod.current = true;
    setPeriod(next);
    setSite('all');
    setType('all');
    pendingSave.current = null;
  };
  const change = (next: HqEmphasisDraft) => {
    setDraft(next);
    setMessage('');
  };
  const update = (activity: HqEmphasisActivity) => {
    if (!draft) return;
    change({
      ...draft,
      activities: draft.activities.some((row) => row.id === activity.id)
        ? draft.activities.map((row) => (row.id === activity.id ? activity : row))
        : [...draft.activities, activity],
    });
  };
  const save = async () => {
    if (!repository || !draft || !canEdit || inputBusy) return;
    const parsed = hqEmphasisDraftSchema.safeParse(draft);
    if (!parsed.success) {
      setError('행사명, 기간, 활동별 지적·조치 건수를 확인해 주세요.');
      return;
    }
    const payload = JSON.stringify({ draft: parsed.data, expectedVersion: document?.version ?? 0 });
    if (pendingSave.current?.payload !== payload)
      pendingSave.current = { payload, commandId: crypto.randomUUID() };
    const run = generation.current;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const result = await repository.save({
        commandId: pendingSave.current.commandId,
        eventId: hqEmphasisEventId(period.year, period.half),
        expectedVersion: document?.version ?? 0,
        draft: parsed.data,
      });
      if (generation.current !== run) return;
      setDocument(result.document);
      setDraft(result.document.draft);
      setBaseline(result.document.draft);
      pendingSave.current = null;
      setMessage('저장했습니다.');
      onSaved?.();
    } catch (failure) {
      if (generation.current === run) {
        const code =
          failure && typeof failure === 'object' && 'code' in failure ? String(failure.code) : '';
        setError(
          code === 'functions/aborted'
            ? '다른 담당자가 먼저 저장했습니다. 입력 내용을 확인한 후 다시 불러와 주세요.'
            : '저장하지 못했습니다. 입력 내용은 유지됩니다. 다시 시도해 주세요.',
        );
      }
    } finally {
      if (generation.current === run) setSaving(false);
    }
  };
  const upload = async (activity: HqEmphasisActivity, files: File[], replace?: string) => {
    if (!repository || !draft || !canEdit || inputBusy || !files.length) return;
    const run = generation.current,
      abort = new AbortController();
    controller.current = abort;
    setUploading(true);
    setError('');
    let next = { ...activity, attachments: [...activity.attachments] };
    try {
      for (const file of replace ? files.slice(0, 1) : files) {
        setMessage(`${file.name} 업로드 중`);
        const attachment = await repository.upload(
          hqEmphasisEventId(period.year, period.half),
          file,
          abort.signal,
          (progress) => {
            if (generation.current === run) setMessage(`${file.name} · ${progress.progress}%`);
          },
        );
        if (generation.current !== run) return;
        const attachments = replace
          ? next.attachments.map((row) => (row.manifestId === replace ? attachment : row))
          : [...next.attachments, attachment];
        next = {
          ...next,
          attachments,
          coverManifestId:
            next.coverManifestId === replace
              ? attachment.kind === 'photo'
                ? attachment.manifestId
                : null
              : next.coverManifestId,
        };
        update(next);
      }
      setMessage('사진·자료를 반영했습니다. 변경사항을 저장해 주세요.');
    } catch (failure) {
      if (generation.current === run)
        setError(
          failure instanceof Error && !('code' in failure)
            ? failure.message
            : '사진·자료를 등록하지 못했습니다. 다시 시도해 주세요.',
        );
    } finally {
      if (generation.current === run) {
        setUploading(false);
        controller.current = null;
      }
    }
  };
  const filtered = (draft?.activities ?? []).filter(
    (row) =>
      (site === 'all' || activityScopeKey(row) === site) && (type === 'all' || row.type === type),
  );
  const siteRows = (draft?.activities ?? []).filter(
    (row) => site === 'all' || activityScopeKey(row) === site,
  );
  const reportScopes = [
    ...new Map(
      (draft?.activities ?? [])
        .filter((row) => !row.siteId)
        .map(
          (row) =>
            [
              activityScopeKey(row),
              { id: activityScopeKey(row), name: divisionLabel(row.division) },
            ] as const,
        ),
    ).values(),
  ];
  const counts = emphasisCounts(siteRows);
  const columns = size.width >= 1000 ? 3 : size.width >= 640 ? 2 : 1;
  const rowsPerPage = size.height >= 650 ? 2 : 1;
  const pageSize =
    tab === 'activities'
      ? Math.max(3, Math.min(8, Math.floor((size.height - 205) / 58)))
      : columns * rowsPerPage;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize)),
    selectedPage = Math.min(page, pageCount - 1);
  const mediaRow = filtered.find((row) => row.id === mediaId) ?? filtered[0];
  const mediaColumns = size.width >= 1000 ? 3 : size.width >= 640 ? 2 : 1;
  const mediaPageCount = Math.max(1, Math.ceil((mediaRow?.attachments.length ?? 0) / mediaColumns));
  const siteName = (id: string | null, division?: HqEmphasisActivity['division']) =>
    id
      ? (loaded?.sites.find((row) => row.id === id)?.name ?? '사업장 확인 필요')
      : divisionLabel(division);
  const mediaInput = (row: HqEmphasisActivity, replace?: string) => (
    <input
      type="file"
      multiple={!replace}
      accept="image/jpeg,image/png,image/webp,application/pdf"
      disabled={!canEdit || inputBusy}
      aria-label={replace ? '첨부 자료 교체' : `${row.title} 사진·자료 추가`}
      onChange={(event) => {
        void upload(row, Array.from(event.target.files ?? []), replace);
        event.target.value = '';
      }}
    />
  );
  const categoryTabs = (
    <nav className="hqe-categories" aria-label="활동 구분">
      {[['all', '전체'], ...activityTypes].map(([id, label]) => (
        <button
          type="button"
          key={id}
          aria-pressed={type === id}
          onClick={() => {
            setType(id);
            setPage(0);
            setMediaPage(0);
            setMediaListPage(0);
          }}
          disabled={inputBusy}
        >
          {label}{' '}
          <small>
            {id === 'all' ? siteRows.length : siteRows.filter((row) => row.type === id).length}
          </small>
        </button>
      ))}
    </nav>
  );
  const closeViewer = useCallback(() => setViewer(null), []);
  return (
    <div
      className="hqe-workspace"
      ref={root}
      aria-busy={busy}
      style={{ '--hqe-columns': columns, '--hqe-rows': rowsPerPage } as CSSProperties}
    >
      <header className="hqe-toolbar">
        <div className="hqe-period-controls">
          <label>
            연도
            <select
              aria-label="강조주간 연도"
              value={period.year}
              disabled={inputBusy}
              onChange={(event) => changePeriod({ ...period, year: Number(event.target.value) })}
            >
              {[
                ...new Set([
                  initialYear,
                  period.year,
                  ...Array.from({ length: 5 }, (_, i) => initialYear - i),
                ]),
              ].map((year) => (
                <option key={year} value={year}>
                  {year}년
                </option>
              ))}
            </select>
          </label>
          <label>
            반기
            <select
              aria-label="강조주간 반기"
              value={period.half}
              disabled={inputBusy}
              onChange={(event) =>
                changePeriod({ ...period, half: Number(event.target.value) as 1 | 2 })
              }
            >
              <option value={1}>상반기</option>
              <option value={2}>하반기</option>
            </select>
          </label>
          <label>
            사업장
            <select
              aria-label="강조주간 사업장"
              value={site}
              disabled={inputBusy}
              onChange={(event) => {
                setSite(event.target.value);
                setPage(0);
                setMediaListPage(0);
                setMediaPage(0);
              }}
            >
              <option value="all">전체 사업장</option>
              {loaded?.sites.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
              {reportScopes.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="hqe-toolbar-actions">
          <span role="status">
            {saving ? '저장 중' : dirty ? '변경사항 있음' : document ? '저장됨' : ''}
          </span>
          <button
            type="button"
            aria-label="강조주간 다시 불러오기"
            disabled={inputBusy}
            onClick={() => {
              if (!dirty || window.confirm('저장하지 않은 변경사항을 버리고 다시 불러올까요?'))
                setRevision((value) => value + 1);
            }}
          >
            <RefreshCw size={15} />
          </button>
          {canEdit && (
            <>
              <button type="button" disabled={inputBusy || !draft} onClick={() => void save()}>
                <Save size={15} />
                변경사항 저장
              </button>
              <button
                type="button"
                className="hqe-primary"
                disabled={inputBusy || !draft}
                onClick={() =>
                  setEditing(
                    newActivity(
                      site === 'all'
                        ? (loaded?.sites[0]?.id ?? null)
                        : loaded?.sites.some((row) => row.id === site)
                          ? site
                          : null,
                    ),
                  )
                }
              >
                <Plus size={15} />
                활동 등록
              </button>
            </>
          )}
        </div>
      </header>
      {draft && (
        <div className="hqe-event-heading">
          <strong>{draft.title}</strong>
          <span>
            {date(draft.eventFrom)} ~ {date(draft.eventTo)}
          </span>
        </div>
      )}
      {(error || message) && (
        <p className={error ? 'hqe-error' : 'hqe-message'} role={error ? 'alert' : 'status'}>
          {error || message}
        </p>
      )}
      <nav className="hqe-tabs" aria-label="강조주간 업무">
        {(
          [
            ['overview', '집계·활동'],
            ['activities', '활동 내역'],
            ['media', '사진·자료 관리'],
            ['plan', '운영계획'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-pressed={tab === id}
            disabled={inputBusy}
            onClick={() => {
              setTab(id);
              setPage(0);
            }}
          >
            {label}
          </button>
        ))}
      </nav>
      {busy ? (
        <div className="hqe-empty" role="status">
          강조주간 자료를 불러오는 중입니다.
        </div>
      ) : !draft || !repository ? (
        <div className="hqe-empty">
          {locked ? '다시 로그인해 주세요.' : error || '강조주간 자료를 조회할 수 없습니다.'}
        </div>
      ) : (
        <>
          {tab === 'overview' && (
            <>
              <section className="hqe-stats" aria-label="선택 반기 활동 집계">
                {[
                  [
                    'inspection',
                    '점검·조치',
                    counts.findings,
                    `조치 ${numeric(counts.actions)}건 · 조치율 ${numeric(counts.rate)}${counts.rate === null ? '' : '%'}`,
                  ],
                  ['drill', '재해대응훈련', counts.drill || null, '개 활동'],
                  ['campaign', '안전캠페인', counts.campaign || null, '개 활동'],
                  ['contest', '안전공모전', counts.contest || null, '개 활동'],
                ].map(([id, label, value, detail]) => (
                  <button
                    type="button"
                    key={String(id)}
                    aria-pressed={type === id}
                    onClick={() => {
                      setType(String(id));
                      setPage(0);
                    }}
                  >
                    <span>{label}</span>
                    <strong>
                      {numeric(value as number | null)}
                      <small>{id === 'inspection' ? '건 지적' : detail}</small>
                    </strong>
                    {id === 'inspection' && <p>{detail}</p>}
                  </button>
                ))}
              </section>
              <div className="hqe-section-heading">
                <h2>
                  활동 내역 <span>{filtered.length}</span>
                </h2>
                <button type="button" onClick={() => setShowTable(true)}>
                  사업장별 집계표 →
                </button>
              </div>
              {categoryTabs}
              <div className="hqe-activity-grid">
                {filtered
                  .slice(selectedPage * pageSize, (selectedPage + 1) * pageSize)
                  .map((row) => {
                    const cover = activityCover(row);
                    return (
                      <article key={row.id} className="hqe-activity-card">
                        <div className="hqe-card-photo">
                          {cover ? (
                            <>
                              <button
                                type="button"
                                aria-label={`${row.title} 사진 확대`}
                                onClick={() => setViewer(cover)}
                              >
                                <EmphasisPhoto
                                  repository={repository}
                                  eventId={hqEmphasisEventId(period.year, period.half)}
                                  attachment={cover}
                                />
                              </button>
                              <button
                                type="button"
                                className="hqe-photo-count"
                                onClick={() => {
                                  setTab('media');
                                  setMediaId(row.id);
                                  setMediaPage(0);
                                  setMediaListPage(
                                    Math.floor(
                                      filtered.findIndex((value) => value.id === row.id) / 6,
                                    ),
                                  );
                                }}
                              >
                                사진{' '}
                                {row.attachments.filter((file) => file.kind === 'photo').length}장 ↗
                              </button>
                            </>
                          ) : (
                            <div className="hqe-photo-placeholder">
                              <ImageIcon size={25} />
                              등록된 사진이 없습니다
                            </div>
                          )}
                        </div>
                        <div className="hqe-card-info">
                          <div>
                            <strong>{siteName(row.siteId, row.division)}</strong>
                            <span>{activityLabel(row.type)}</span>
                            <time>{date(row.date)}</time>
                          </div>
                          <h3>
                            <button type="button" onClick={() => setEditing(structuredClone(row))}>
                              {row.title}
                            </button>
                          </h3>
                          <p title={row.note}>{row.note || '등록된 상세 내용이 없습니다.'}</p>
                          <footer>
                            <span>
                              {row.type === 'inspection' ? (
                                <>
                                  지적 <b>{numeric(row.findings)}</b>건 · 조치{' '}
                                  <b>{numeric(row.actions)}</b>건
                                </>
                              ) : (
                                row.owner || '담당 미지정'
                              )}
                            </span>
                            <button type="button" onClick={() => setEditing(structuredClone(row))}>
                              활동 상세 →
                            </button>
                          </footer>
                        </div>
                      </article>
                    );
                  })}
              </div>
              {!filtered.length && (
                <div className="hqe-empty">선택한 조건에 등록된 활동이 없습니다.</div>
              )}
              <Pager
                page={selectedPage}
                pages={pageCount}
                total={filtered.length}
                onChange={setPage}
              />
            </>
          )}
          {tab === 'activities' && (
            <>
              {categoryTabs}
              <div className="hqe-table-area">
                <table>
                  <thead>
                    <tr>
                      <th>사업장</th>
                      <th>활동</th>
                      <th>실시일</th>
                      <th>지적</th>
                      <th>조치</th>
                      <th>자료</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered
                      .slice(selectedPage * pageSize, (selectedPage + 1) * pageSize)
                      .map((row) => (
                        <tr key={row.id}>
                          <th>{siteName(row.siteId, row.division)}</th>
                          <td>
                            <small>{activityLabel(row.type)}</small>
                            <button type="button" onClick={() => setEditing(structuredClone(row))}>
                              {row.title}
                            </button>
                          </td>
                          <td>{date(row.date)}</td>
                          <td>{numeric(row.findings)}</td>
                          <td>{numeric(row.actions)}</td>
                          <td>
                            <button
                              type="button"
                              onClick={() => {
                                setTab('media');
                                setMediaId(row.id);
                                setMediaPage(0);
                                setMediaListPage(
                                  Math.floor(
                                    filtered.findIndex((value) => value.id === row.id) / 6,
                                  ),
                                );
                              }}
                            >
                              {row.attachments.length}개
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {!filtered.length && <div className="hqe-empty">등록된 활동이 없습니다.</div>}
              </div>
              <Pager
                page={selectedPage}
                pages={pageCount}
                total={filtered.length}
                onChange={setPage}
              />
            </>
          )}
          {tab === 'media' && (
            <>
              {categoryTabs}
              <div className="hqe-media-workspace">
                <aside>
                  <header>
                    <strong>활동 선택</strong>
                    <small>{filtered.length}개</small>
                  </header>
                  <div>
                    {filtered.slice(mediaListPage * 6, mediaListPage * 6 + 6).map((row) => (
                      <button
                        type="button"
                        key={row.id}
                        aria-pressed={row.id === mediaRow?.id}
                        disabled={inputBusy}
                        onClick={() => {
                          setMediaId(row.id);
                          setMediaPage(0);
                        }}
                      >
                        <small>
                          {siteName(row.siteId, row.division)} · {activityLabel(row.type)}
                        </small>
                        <strong>{row.title}</strong>
                      </button>
                    ))}
                  </div>
                  <Pager
                    page={mediaListPage}
                    pages={Math.max(1, Math.ceil(filtered.length / 6))}
                    total={filtered.length}
                    onChange={setMediaListPage}
                  />
                </aside>
                <section className="hqe-media-panel">
                  {mediaRow ? (
                    <>
                      <header>
                        <div>
                          <small>
                            {siteName(mediaRow.siteId, mediaRow.division)} ·{' '}
                            {activityLabel(mediaRow.type)} · {date(mediaRow.date)}
                          </small>
                          <h2>{mediaRow.title}</h2>
                        </div>
                        <div>
                          {canEdit && (
                            <label className="hqe-upload">
                              사진·자료 추가 +{mediaInput(mediaRow)}
                            </label>
                          )}
                          <button
                            type="button"
                            disabled={inputBusy}
                            onClick={() => setEditing(structuredClone(mediaRow))}
                          >
                            {canEdit ? '활동 수정' : '활동 상세'}
                          </button>
                        </div>
                      </header>
                      <div
                        className="hqe-managed-files"
                        style={{ '--hqe-media-columns': mediaColumns } as CSSProperties}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          void upload(mediaRow, Array.from(event.dataTransfer.files));
                        }}
                      >
                        {mediaRow.attachments
                          .slice(
                            Math.min(mediaPage, mediaPageCount - 1) * mediaColumns,
                            (Math.min(mediaPage, mediaPageCount - 1) + 1) * mediaColumns,
                          )
                          .map((file) => (
                            <article key={file.manifestId}>
                              <button
                                type="button"
                                className="hqe-file-preview"
                                aria-label={`${file.fileName} 확대`}
                                onClick={() => setViewer(file)}
                              >
                                {file.kind === 'photo' ? (
                                  <EmphasisPhoto
                                    repository={repository}
                                    eventId={hqEmphasisEventId(period.year, period.half)}
                                    attachment={file}
                                  />
                                ) : (
                                  <span>PDF</span>
                                )}
                              </button>
                              <p title={file.fileName}>{file.fileName}</p>
                              {canEdit && (
                                <footer>
                                  {file.kind === 'photo' ? (
                                    <button
                                      type="button"
                                      disabled={inputBusy}
                                      aria-pressed={
                                        activityCover(mediaRow)?.manifestId === file.manifestId
                                      }
                                      onClick={() =>
                                        update({ ...mediaRow, coverManifestId: file.manifestId })
                                      }
                                    >
                                      {activityCover(mediaRow)?.manifestId === file.manifestId
                                        ? '✓ 대표사진'
                                        : '대표 지정'}
                                    </button>
                                  ) : (
                                    <span />
                                  )}
                                  <label className="hqe-upload">
                                    교체{mediaInput(mediaRow, file.manifestId)}
                                  </label>
                                  <button
                                    type="button"
                                    disabled={inputBusy}
                                    onClick={() => {
                                      update({
                                        ...mediaRow,
                                        attachments: mediaRow.attachments.filter(
                                          (row) => row.manifestId !== file.manifestId,
                                        ),
                                        coverManifestId:
                                          mediaRow.coverManifestId === file.manifestId
                                            ? null
                                            : mediaRow.coverManifestId,
                                      });
                                      setMediaPage(0);
                                    }}
                                  >
                                    삭제
                                  </button>
                                </footer>
                              )}
                            </article>
                          ))}
                        {!mediaRow.attachments.length && (
                          <div className="hqe-empty">
                            <ImageIcon size={30} />
                            등록된 사진·자료가 없습니다.
                            {canEdit && (
                              <label className="hqe-upload">
                                사진·자료 추가{mediaInput(mediaRow)}
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                      <Pager
                        page={Math.min(mediaPage, mediaPageCount - 1)}
                        pages={mediaPageCount}
                        total={mediaRow.attachments.length}
                        onChange={setMediaPage}
                      />
                    </>
                  ) : (
                    <div className="hqe-empty">활동을 등록한 뒤 사진·자료를 추가해 주세요.</div>
                  )}
                </section>
              </div>
            </>
          )}
          {tab === 'plan' && (
            <section className="hqe-plan">
              <nav>
                <button type="button" aria-pressed={planStep === 0} onClick={() => setPlanStep(0)}>
                  기본정보·기간
                </button>
                <button type="button" aria-pressed={planStep === 1} onClick={() => setPlanStep(1)}>
                  중점사항·참여 사업장
                </button>
              </nav>
              <fieldset disabled={!canEdit || inputBusy}>
                {planStep === 0 ? (
                  <>
                    <label>
                      행사명
                      <input
                        aria-label="행사명"
                        value={draft.title}
                        maxLength={300}
                        onChange={(event) => change({ ...draft, title: event.target.value })}
                      />
                    </label>
                    <div className="hqe-fields-two">
                      <label>
                        시작일
                        <input
                          aria-label="시작일"
                          type="date"
                          value={draft.eventFrom ?? ''}
                          onChange={(event) =>
                            change({ ...draft, eventFrom: event.target.value || null })
                          }
                        />
                      </label>
                      <label>
                        종료일
                        <input
                          aria-label="종료일"
                          type="date"
                          value={draft.eventTo ?? ''}
                          onChange={(event) =>
                            change({ ...draft, eventTo: event.target.value || null })
                          }
                        />
                      </label>
                    </div>
                    <label>
                      총괄 담당
                      <input
                        aria-label="총괄 담당"
                        value={draft.owner}
                        maxLength={200}
                        onChange={(event) => change({ ...draft, owner: event.target.value })}
                      />
                    </label>
                  </>
                ) : (
                  <>
                    <label>
                      운영 목적·중점사항
                      <textarea
                        aria-label="운영 목적·중점사항"
                        value={draft.objective}
                        maxLength={5000}
                        onChange={(event) => change({ ...draft, objective: event.target.value })}
                      />
                    </label>
                    <div className="hqe-participants">
                      <strong>참여 사업장</strong>
                      {loaded?.sites.map((row) => (
                        <label key={row.id}>
                          <input
                            type="checkbox"
                            checked={draft.siteIds.includes(row.id)}
                            onChange={(event) =>
                              change({
                                ...draft,
                                siteIds: event.target.checked
                                  ? [...draft.siteIds, row.id]
                                  : draft.siteIds.filter((id) => id !== row.id),
                              })
                            }
                          />
                          {row.name}
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </fieldset>
              <footer>
                <span>{canEdit ? '상단의 변경사항 저장을 눌러 반영합니다.' : '조회 전용'}</span>
                <button type="button" onClick={() => setPlanStep((value) => (value === 0 ? 1 : 0))}>
                  {planStep === 0 ? '중점사항·참여 사업장 →' : '← 기본정보·기간'}
                </button>
              </footer>
            </section>
          )}
        </>
      )}
      {editing && (
        <ActivityEditor
          key={editing.id}
          activity={editing}
          sites={loaded?.sites ?? []}
          canEdit={canEdit && !inputBusy}
          onClose={() => setEditing(null)}
          onApply={(row) => {
            update(row);
            setEditing(null);
          }}
        />
      )}
      {showTable && draft && (
        <AggregateDialog
          activities={siteRows}
          sites={(loaded?.sites ?? []).filter((row) => site === 'all' || row.id === site)}
          onClose={() => setShowTable(false)}
        />
      )}
      {viewer && repository && (
        <EmphasisFileViewer
          key={viewer.manifestId}
          repository={repository}
          eventId={hqEmphasisEventId(period.year, period.half)}
          attachment={viewer}
          onClose={closeViewer}
        />
      )}
    </div>
  );
}

function Pager({
  page,
  pages,
  total,
  onChange,
}: {
  page: number;
  pages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  return (
    <footer className="hqe-pager">
      <span>전체 {total}개</span>
      <div>
        <button
          type="button"
          aria-label="이전 페이지"
          disabled={page === 0}
          onClick={() => onChange(page - 1)}
        >
          <ChevronLeft size={15} />
        </button>
        <span>
          {page + 1} / {pages}
        </span>
        <button
          type="button"
          aria-label="다음 페이지"
          disabled={page + 1 >= pages}
          onClick={() => onChange(page + 1)}
        >
          <ChevronRight size={15} />
        </button>
      </div>
    </footer>
  );
}
function ActivityEditor({
  activity,
  sites,
  canEdit,
  onClose,
  onApply,
}: {
  activity: HqEmphasisActivity;
  sites: readonly { id: string; name: string }[];
  canEdit: boolean;
  onClose: () => void;
  onApply: (row: HqEmphasisActivity) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null),
    [value, setValue] = useState(activity),
    [error, setError] = useState('');
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  return createPortal(
    <dialog
      className="hqe-dialog hqe-activity-editor"
      ref={dialog}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header>
        <h2>{canEdit ? '활동 관리' : '활동 상세'}</h2>
        <button type="button" onClick={onClose} aria-label="활동 창 닫기">
          <X size={20} />
        </button>
      </header>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!canEdit) return;
          const parsed = hqEmphasisActivitySchema.safeParse(value);
          if (!parsed.success) {
            setError(
              '활동명과 지적·조치 건수를 확인해 주세요. 조치 건수는 지적 건수를 넘을 수 없습니다.',
            );
            return;
          }
          onApply(parsed.data);
        }}
      >
        <fieldset disabled={!canEdit}>
          <div className="hqe-fields-two">
            <label>
              활동 구분
              <select
                aria-label="활동 구분"
                value={value.type}
                onChange={(event) =>
                  setValue({
                    ...value,
                    type: event.target.value as HqEmphasisActivity['type'],
                    findings: null,
                    actions: null,
                  })
                }
              >
                {activityTypes.map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              사업장
              <select
                aria-label="활동 사업장"
                value={value.siteId ?? (value.division ? `division:${value.division}` : '')}
                onChange={(event) => {
                  if (!event.target.value.startsWith('division:'))
                    setValue({ ...value, siteId: event.target.value || null, division: null });
                }}
              >
                <option value="">공통</option>
                {value.division && !value.siteId && (
                  <option value={`division:${value.division}`}>
                    {divisionLabel(value.division)}
                  </option>
                )}
                {sites.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            활동명
            <input
              aria-label="활동명"
              required
              maxLength={300}
              value={value.title}
              onChange={(event) => setValue({ ...value, title: event.target.value })}
            />
          </label>
          <div className="hqe-fields-two">
            <label>
              실시일
              <input
                aria-label="실시일"
                type="date"
                value={value.date ?? ''}
                onChange={(event) => setValue({ ...value, date: event.target.value || null })}
              />
            </label>
            <label>
              담당
              <input
                aria-label="담당"
                maxLength={200}
                value={value.owner}
                onChange={(event) => setValue({ ...value, owner: event.target.value })}
              />
            </label>
          </div>
          {value.type === 'inspection' && (
            <div className="hqe-fields-two">
              {(['findings', 'actions'] as const).map((key) => (
                <label key={key}>
                  {key === 'findings' ? '지적 건수' : '조치 건수'}
                  <input
                    aria-label={key === 'findings' ? '지적 건수' : '조치 건수'}
                    type="number"
                    min={0}
                    max={1000000}
                    step={1}
                    value={value[key] ?? ''}
                    placeholder="미확정"
                    onChange={(event) =>
                      setValue({
                        ...value,
                        [key]: event.target.value === '' ? null : Number(event.target.value),
                      })
                    }
                  />
                </label>
              ))}
            </div>
          )}
          <label>
            주요 내용·조치사항
            <textarea
              aria-label="주요 내용·조치사항"
              maxLength={5000}
              value={value.note}
              onChange={(event) => setValue({ ...value, note: event.target.value })}
            />
          </label>
        </fieldset>
        {error && (
          <p role="alert" className="hqe-error">
            {error}
          </p>
        )}
        <footer>
          <span>현장사진은 사진·자료 관리에서 등록합니다.</span>
          <button type="button" onClick={onClose}>
            {canEdit ? '취소' : '닫기'}
          </button>
          {canEdit && (
            <button type="submit" className="hqe-primary">
              활동 반영
            </button>
          )}
        </footer>
      </form>
    </dialog>,
    window.document.body,
  );
}
function AggregateDialog({
  activities,
  sites,
  onClose,
}: {
  activities: HqEmphasisActivity[];
  sites: readonly { id: string; name: string }[];
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null),
    [type, setType] = useState<HqEmphasisActivity['type']>('inspection'),
    [page, setPage] = useState(0);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  const allSites = [
    ...sites,
    ...new Map(
      activities
        .filter((row) => !row.siteId)
        .map(
          (row) =>
            [
              activityScopeKey(row),
              { id: activityScopeKey(row), name: divisionLabel(row.division) },
            ] as const,
        ),
    ).values(),
  ];
  return createPortal(
    <dialog
      ref={dialog}
      className="hqe-dialog hqe-aggregate-dialog"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header>
        <h2>사업장별 집계표</h2>
        <button type="button" onClick={onClose} aria-label="집계표 닫기">
          <X size={20} />
        </button>
      </header>
      <nav className="hqe-tabs">
        {activityTypes.map(([id, label]) => (
          <button
            type="button"
            key={id}
            aria-pressed={type === id}
            onClick={() => {
              setType(id);
              setPage(0);
            }}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="hqe-table-area">
        <table>
          <thead>
            <tr>
              <th>사업장</th>
              {type === 'inspection' ? (
                <>
                  <th>지적</th>
                  <th>조치</th>
                  <th>미조치</th>
                  <th>조치율</th>
                </>
              ) : (
                <>
                  <th>활동</th>
                  <th>최근 실시일</th>
                </>
              )}
              <th>사진·자료</th>
            </tr>
          </thead>
          <tbody>
            {allSites.slice(page * 6, page * 6 + 6).map((site) => {
              const rows = activities.filter(
                  (row) => activityScopeKey(row) === site.id && row.type === type,
                ),
                counts = emphasisCounts(rows);
              return (
                <tr key={site.id}>
                  <th>{site.name}</th>
                  {type === 'inspection' ? (
                    <>
                      <td>{numeric(counts.findings)}</td>
                      <td>{numeric(counts.actions)}</td>
                      <td>{numeric(counts.outstanding)}</td>
                      <td>{counts.rate === null ? '—' : `${counts.rate}%`}</td>
                    </>
                  ) : (
                    <>
                      <td>{rows.length || '—'}</td>
                      <td>
                        {rows.length
                          ? date(
                              rows
                                .map((row) => row.date)
                                .filter((value): value is string => !!value)
                                .sort()
                                .at(-1) ?? null,
                            )
                          : '—'}
                      </td>
                    </>
                  )}
                  <td>{rows.flatMap((row) => row.attachments).length || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager
        page={page}
        pages={Math.max(1, Math.ceil(allSites.length / 6))}
        total={allSites.length}
        onChange={setPage}
      />
    </dialog>,
    window.document.body,
  );
}
